import { Button, useMultiStepForm, V2Loader, cn } from "web-design-system";
import {
  useInvestorPrimaryBankAccount,
  useNotifyInvestorMutation,
  useOnboardingChecklist,
  useSendKycEmail,
} from "creso-core/hooks";
import { toast } from "sonner";
import { getOnboardingSteps, isMinor, isNRI } from "creso-core/utils";
import { CHECKLIST_STEP_CODES, getChecklistStepId } from "creso-core/constants";
import {
  WarningLottieAnimation,
  useClientOnboarding,
  isNewGuardian,
  useMinorOnboarding,
} from "web-core/components";
import { BankIcon } from "@core/components/icon/bank-icon";
import { CopyableText } from "@core/components/copyable-text";
import { handleError } from "@/utils/error";
import { CheckIcon, CircleAlert, IdCard } from "lucide-react";

export function NotifyClient() {
  const { client, close } = useClientOnboarding();
  const { guardian } = useMinorOnboarding();
  const steps = getOnboardingSteps(client, {
    newGuardian: isNewGuardian(guardian),
  });
  const stepIndex = (id?: string) => steps.findIndex((step) => step.id === id);
  // NRI clients have an extra step before Bank details
  const bankDetailsStep = stepIndex("bank-details");
  const { goToStep } = useMultiStepForm();
  // For a minor, the guardian is the one who finishes setup
  const minor = isMinor(client);
  const recipientName = minor
    ? (guardian?.name ?? "the guardian")
    : client?.name;
  const minorFirstName = client?.name?.split(" ")[0] ?? "";
  const hasChecklist = minor || isNRI(client ?? undefined);
  const { data: checklist, isLoading: isLoadingChecklist } =
    useOnboardingChecklist(client?.id ?? 0, hasChecklist);
  // Bank and KYC have their own cards below
  const checklistItems =
    checklist?.missing.filter(
      (item) =>
        !CHECKLIST_STEP_CODES["bank-details"].includes(item.code) &&
        !item.code.endsWith("KYC_NOT_VERIFIED")
    ) ?? [];

  const { data: bankDetails, isLoading: isLoadingBank } =
    useInvestorPrimaryBankAccount(client?.investment_account ?? "");
  const isLoading = isLoadingBank || (hasChecklist && isLoadingChecklist);

  const { mutateAsync: sendKycEmail, isPending } = useSendKycEmail();

  const { mutate: notifyInvestor, isPending: isNotifyingInvestor } =
    useNotifyInvestorMutation();

  const handleNotifyInvestor = () => {
    notifyInvestor(client?.investment_account ?? "", {
      onSuccess: () => {
        toast.success(
          minor
            ? "Guardian notified successfully"
            : "Client notified successfully"
        );
        close();
      },
      onError: (error) => handleError(error, "Failed to notify"),
    });
  };

  const isOnboardingComplete =
    bankDetails?.verification_status === "verified" &&
    client?.kyc_status === "S" &&
    (!hasChecklist || !!checklist?.ready);

  if (!isLoading && isOnboardingComplete) {
    return (
      <div className="flex flex-col justify-between p-5 h-full">
        <div className="flex flex-col gap-12">
          <div className="flex flex-col items-center gap-5 pt-8">
            <div className="flex items-center justify-center size-24 rounded-full bg-tag-green">
              <CheckIcon
                className="size-10 text-tag-green-foreground"
                strokeWidth={2.5}
              />
            </div>
            <h4>Setup complete</h4>
          </div>
          <div className="flex flex-col gap-3">
            <span className="text-sm font-semibold">
              Next steps. Ask {recipientName} to:
            </span>
            <div className="flex flex-col gap-3 rounded-xl bg-tag-slate p-5 text-sm">
              <span className="flex flex-wrap items-center gap-x-2">
                1. Download the Creso app
                <CopyableText
                  className="underline text-sm"
                  label="Share app link"
                  text="https://4bmjx.app.link/creso-app"
                />
              </span>
              <span>
                2. Verify {minor ? `${minorFirstName}'s` : "their"} details
              </span>
              <span>3. Authenticate with OTP</span>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button
            onClick={handleNotifyInvestor}
            disabled={isNotifyingInvestor}
            className="w-full"
            size="lg"
          >
            {minor ? "Notify guardian" : "Notify client"}
          </Button>
          <Button variant="link" onClick={() => goToStep(0)}>
            Edit details
          </Button>
        </div>
      </div>
    );
  }

  return (
    <V2Loader
      isLoading={isLoading}
      className="flex flex-col items-center justify-center p-5 h-full"
    >
      <div className="flex-1 flex flex-col items-center justify-start size-full">
        <div className="flex items-center gap-4 flex-col justify-center mt-10">
          <WarningLottieAnimation loop={false} height={100} width={100} />
          <h4>Setup incomplete</h4>
        </div>
        <div className={cn("h-px bg-border w-full my-4")} />
        {!isOnboardingComplete && (
          <div className="flex flex-col text-sm gap-4 w-full">
            <h6 className="text-sm font-medium">Pending items</h6>
            {checklistItems.map((item) => {
              const step = stepIndex(getChecklistStepId(item.code));
              return (
                <div
                  key={`${item.code}-${item.profile_id ?? ""}`}
                  className="flex flex-row gap-2 w-full bg-tag-orange rounded-lg p-3 items-start"
                >
                  <CircleAlert className="size-4 mt-0.5 shrink-0 text-tag-orange-foreground" />
                  <span className="text-sm leading-5 text-tag-orange-foreground">
                    {item.message}
                  </span>
                  {step >= 0 && (
                    <Button
                      size="sm"
                      className="ml-auto font-semibold h-auto p-0 shrink-0"
                      variant="link"
                      onClick={() => goToStep(step)}
                    >
                      Edit
                    </Button>
                  )}
                </div>
              );
            })}
            {client?.kyc_status === "F" && (
              <div className="flex flex-col gap-2 w-full bg-tag-orange rounded-lg p-3">
                <div className="flex font-semibold flex-row w-full gap-2 items-center">
                  <IdCard className="size-4 text-tag-orange-foreground" />
                  <span className="text-sm tracking-tight font-semibold text-tag-orange-foreground">
                    KYC pending
                  </span>
                  <Button
                    size="sm"
                    className="ml-auto font-semibold h-auto p-0 shrink-0"
                    variant="link"
                    disabled={isPending}
                    onClick={() =>
                      sendKycEmail(client.investment_account ?? "").catch(
                        (error) => handleError(error, "Failed to send KYC link")
                      )
                    }
                  >
                    Notify client
                  </Button>
                </div>
                <span className="text-sm leading-5 text-muted-foreground">
                  Your client needs to complete KYC process, notify them with
                  instructions.
                </span>
              </div>
            )}
            {bankDetails?.verification_status !== "verified" && (
              <div className="flex flex-col gap-2 w-full bg-tag-orange rounded-lg p-3">
                <div className="flex font-semibold flex-row w-full gap-2 items-center">
                  <BankIcon className="stroke-tag-orange-foreground" />
                  <span className="text-sm tracking-tight font-semibold text-tag-orange-foreground">
                    Bank details
                  </span>
                  <Button
                    size="sm"
                    className="ml-auto font-semibold h-auto p-0 shrink-0"
                    variant="link"
                    onClick={() => goToStep(bankDetailsStep)}
                  >
                    Edit
                  </Button>
                </div>
                <span className="text-sm leading-5 text-muted-foreground">
                  {bankDetails?.verification_status === "unverified"
                    ? "We couldn't verify the account at this point, please try again later."
                    : (bankDetails?.verification_remark ??
                      "Bank account verification failed.")}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
      {bankDetails?.verification_status === "unverified" && (
        <Button onClick={close} className="w-full" size="lg">
          Close
        </Button>
      )}
    </V2Loader>
  );
}
